/**
 * CR220: Central UI Shaping Layer - toUiDeal
 * 
 * Single source of truth for converting ANY deal record into the canonical
 * UI shape used by DealGiftCard and all preview surfaces.
 * 
 * This mapper ensures consistent behavior between:
 * - Public site (usePublicDeals)
 * - Layout Studio preview (useLayoutPreviewDeals)
 * - Admin preview (staging, batch, admin, live modes)
 * - Mock data
 * 
 * Field resolution priorities:
 * - Dates: top-level valid_from/valid_until → canonical_deal_json.dates → evidence
 * - Values: top-level computed_value_cad → canonical nested → null
 * - Brands: reward_brand_name → purchase_gift_card_brand → bonus_merchant
 */

import type { Deal, DealCategory, DealArtifacts, SourceType, AudienceHint, EligibilityScope, AvailabilityScope, DatesType } from "@/lib/types/deals";
import { isSafeValue, getSafeString, sanitizeDisplayText } from "./safeValueRendering";
import { lookupByProgramName, calculatePointsCashValue } from "./loyaltyProgramLookup";
import { formatEligibilityRange } from "./formatEligibilityRange";
import { toDateOnlyString } from "@/lib/dates/dateOnly";

// ================================================================
// INPUT TYPES - What we accept from various sources
// ================================================================

/**
 * Flexible input type that accepts deals from any source:
 * - API responses (admin-preview-deals, public-deals-available)
 * - Staging table rows (cr075_staging_deals_deduped)
 * - Production table rows (cr075_production_deals)
 * - Mock data
 */
export interface RawDealInput {
  // Identity
  id?: string;
  dedupe_key?: string;
  
  // Merchant/Brand (multiple possible field names)
  merchant?: string;
  merchant_name?: string;
  merchant_key?: string;
  promo_merchant_name?: string;
  reward_brand_name?: string | null;
  purchase_gift_card_brand?: string | null;
  gift_card_brand?: string | null;
  qualifying_product_name?: string | null;
  bonus_merchant?: string | null;
  // CR279: Nested brand fields from v_public_active_deals view
  nested_purchase_brand?: string | null;
  nested_bonus_brand?: string | null;
  // CR417: Server-computed heading brand for staging/admin preview
  header_brand_name?: string | null;
  
  // Deal type
  deal_type?: string | null;
  deal_subtype?: string | null;
  deal_category?: DealCategory | string;
  
  // Mechanic / Headline
  headline_mechanic?: string | null;
  
  // Value fields
  bonus_amount?: number | null;
  bonus_value_cad?: number | null;
  spend_amount?: number | null;
  qualifying_spend_cad?: number | null;
  discount_percent?: number | null;
  points_amount?: number | null;
  points_program_name?: string | null;
  computed_value_cad?: number | null;
  // CR313: Discount fields from raw inputs
  pay_amount_cad?: number | null;
  receive_value_cad?: number | null;
  discount_amount_cad?: number | null;
  effective_percent_back?: number | null;
  
  // Dates (multiple possible field names)
  start_date?: string | null;
  end_date?: string | null;
  valid_from?: string | null;
  valid_until?: string | null;
  // CR412: Date-only columns from v_deals_live / v_public_active_deals
  valid_from_date?: string | null;  // "YYYY-MM-DD"
  valid_until_date?: string | null; // "YYYY-MM-DD"
  
  // Source
  source_type?: string | null;
  source_url?: string | null;
  flyer_deeplink_url?: string | null;
  flyer_page_number?: number | null;
  flyer_page_position?: string | null;
  
  // Actions
  claim_url?: string | null;
  deal_url?: string | null;
  deal_link?: string | null;
  
  // Meta
  fine_print?: string | null;
  exclusions?: string | null;
  confidence?: "High" | "Medium" | "Low" | number | null;
  region?: string | null;
  
  // Location/Eligibility
  availability_scope?: string | null;
  availability_notes?: string | null;
  eligibility_scope?: string | null;
  audience_hint?: string | null;
  
  // Review status (staging mode)
  review_status?: string | null;
  
  // Policy proof (for admin debug)
  policy_config_id?: string | null;
  policy_version?: string | null;
  
  // Nested canonical (from raw table queries)
  canonical_deal_json?: Record<string, unknown>;
  
  // Evidence (from staging items)
  evidence_json?: Record<string, unknown>;
  
  // CR292: Date type fields from prompt pack extraction
  dates_type?: DatesType | string | null;
  date_phrase?: string | null;
  
  // CR415: Issuer fields from enriched live view
  issuer_display_name?: string | null;
  issuer_logo_url?: string | null;
  issuer_merchant_key?: string | null;
}

// ================================================================
// OUTPUT TYPE - UIDeal
// ================================================================

/** Tier used for date resolution - for debugging */
export type DateSourceTier = "top_level" | "top_level_legacy" | "canonical_root" | "canonical_dates" | "evidence" | "none";

/**
 * Canonical UI deal shape that DealGiftCard expects.
 * Extends Deal but adds _previewId for React keys.
 */
export interface UIDeal extends Deal {
  /** Internal ID for React keys - always present */
  _previewId: string;
  /** Review status for staging mode */
  _reviewStatus?: string | null;
  /** Source mode for debugging */
  _sourceMode?: "live" | "admin" | "staging" | "batch" | "mock";
  
  // CR220: Debug guardrails
  /** Which tier was used to resolve dates */
  _dateSourceTier?: DateSourceTier;
  
  // CR313: Preserve canonical_deal_json for V2 block resolvers (value_box, dates, etc.)
  canonical_deal_json?: Record<string, unknown>;
  // CR279: Variant-level purchase brand from nested source shapes (admin/staging views)
  nested_purchase_brand?: string | null;
}

// ================================================================
// NORMALIZATION HELPERS
// ================================================================

const VALID_CATEGORIES: DealCategory[] = ["BONUS", "DISCOUNT", "POINTS", "IN_KIND", "UNCLASSIFIED"];

/**
 * Normalize deal type string to DealCategory enum
 */
function normalizeDealCategory(value: string | null | undefined): DealCategory {
  if (!value) return "UNCLASSIFIED";
  const upper = value.toUpperCase().replace(/-/g, "_");
  if (VALID_CATEGORIES.includes(upper as DealCategory)) {
    return upper as DealCategory;
  }
  // CR335: Handle common variations — canonical mapping
  if (upper === "IN-KIND" || upper === "INKIND") return "IN_KIND";
  if (upper === "GIFT" || upper === "BONUS_CARD" || upper === "BONUS_SAME" || upper === "BONUS_DIFFERENT" || upper === "BONUS_SAME_BACK") return "BONUS";
  if (upper === "PERCENT_BACK") return "DISCOUNT";
  if (upper === "LOYALTY_POINTS") return "POINTS";
  return "UNCLASSIFIED";
}

/**
 * Normalize confidence to display value
 */
function normalizeConfidence(conf: "High" | "Medium" | "Low" | number | null | undefined): "High" | "Medium" | "Low" {
  if (typeof conf === "string") {
    if (conf === "High" || conf === "Medium" || conf === "Low") return conf;
    return "Low";
  }
  if (typeof conf === "number") {
    if (conf >= 0.8) return "High";
    if (conf >= 0.5) return "Medium";
    return "Low";
  }
  return "Low";
}

/**
 * Date resolution result with tier tracking for debugging
 */
interface DateResolutionResult {
  startDate: string | null;
  endDate: string | null;
  tier: DateSourceTier;
  datesType: DatesType | null;
  datePhrase: string | null;
  dateMode: "ONGOING" | "FIXED" | null;
}

/**
 * Resolve dates from multiple possible sources with priority:
 * 1. Top-level valid_from/valid_until (API response)
 * 2. Top-level start_date/end_date (legacy/mock)
 * 3. canonical_deal_json.valid_from/valid_until
 * 4. canonical_deal_json.dates.valid_from/valid_until
 * 5. evidence_json dates (if present)
 * 
 * CR313: Now resolves dates FIRST, then derives dates_type/date_phrase from them.
 * Returns tier used for debugging guardrails.
 */
function resolveDates(input: RawDealInput): DateResolutionResult {
  // First, resolve the actual date values
  let startDate: string | null = null;
  let endDate: string | null = null;
  let tier: DateSourceTier = "none";
  
  // CR412: Priority 0 — Date-only columns (timezone-safe, preferred)
  if (isSafeValue(input.valid_from_date) || isSafeValue(input.valid_until_date)) {
    startDate = getSafeString(input.valid_from_date);
    endDate = getSafeString(input.valid_until_date);
    tier = "top_level";
  }
  // Priority 1: Top-level valid_from/valid_until (convert to date-only to prevent TZ shift)
  else if (isSafeValue(input.valid_from) || isSafeValue(input.valid_until)) {
    startDate = toDateOnlyString(input.valid_from as string) ?? getSafeString(input.valid_from);
    endDate = toDateOnlyString(input.valid_until as string) ?? getSafeString(input.valid_until);
    tier = "top_level";
  }
  // Priority 2: Top-level start_date/end_date
  else if (isSafeValue(input.start_date) || isSafeValue(input.end_date)) {
    startDate = getSafeString(input.start_date);
    endDate = getSafeString(input.end_date);
    tier = "top_level_legacy";
  }
  // Priority 3: canonical_deal_json root level
  else {
    const canonical = input.canonical_deal_json;
    if (canonical) {
      if (isSafeValue(canonical.valid_from) || isSafeValue(canonical.valid_until)) {
        startDate = getSafeString(canonical.valid_from);
        endDate = getSafeString(canonical.valid_until);
        tier = "canonical_root";
      } else {
        // Priority 4: canonical_deal_json.dates nested object
        const dates = canonical.dates as Record<string, unknown> | undefined;
        if (dates && (isSafeValue(dates.valid_from) || isSafeValue(dates.valid_until))) {
          startDate = getSafeString(dates.valid_from);
          endDate = getSafeString(dates.valid_until);
          tier = "canonical_dates";
        }
      }
    }
  }
  
  // Priority 5: evidence_json (staging items) - only if nothing found yet
  if (tier === "none") {
    const evidence = input.evidence_json;
    if (evidence) {
      const evidenceDates = evidence.dates as Record<string, unknown> | undefined;
      if (evidenceDates) {
        startDate = getSafeString(evidenceDates.valid_from) || getSafeString(evidenceDates.start_date);
        endDate = getSafeString(evidenceDates.valid_until) || getSafeString(evidenceDates.end_date);
        if (startDate || endDate) {
          tier = "evidence";
        }
      }
    }
  }
  
  // CR313: Now resolve dates_type/date_phrase/date_mode with knowledge of resolved dates
  const { datesType, datePhrase, dateMode } = resolveDatesTypeInfo(input, startDate, endDate);
  
  return { startDate, endDate, tier, datesType, datePhrase, dateMode };
}

/**
 * CR292: Normalize dates_type string to valid DatesType enum value
 */
function normalizeDatesType(raw: string | null | undefined): DatesType | null {
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (upper === "FIXED_WINDOW" || upper === "EVERGREEN" || upper === "UNKNOWN") {
    return upper as DatesType;
  }
  return null;
}

/**
 * CR292: Normalize date_mode string to valid DateMode enum value
 */
function normalizeDateMode(raw: string | null | undefined): "ONGOING" | "FIXED" | null {
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (upper === "ONGOING" || upper === "FIXED") {
    return upper as "ONGOING" | "FIXED";
  }
  return null;
}

/**
 * CR313: Resolve dates_type, date_phrase, and date_mode from input sources
 * 
 * DERIVATION RULES (CR313):
 * 1. dates_type: Use explicit value if present, else derive from date_mode:
 *    - date_mode="FIXED" → dates_type="FIXED_WINDOW"
 *    - date_mode="ONGOING" → dates_type="EVERGREEN"
 *    - If no mode but has valid_from+valid_until → infer "FIXED_WINDOW"
 * 
 * 2. date_phrase: Use explicit value if present, else derive from dates:
 *    - FIXED + range → formatEligibilityRange output
 *    - ONGOING → "Ongoing"
 * 
 * Priority for explicit values:
 * 1. Top-level dates_type/date_phrase/date_mode
 * 2. canonical_deal_json.dates.dates_type/date_phrase/date_mode
 * 3. evidence_json.dates.dates_type/date_phrase/date_mode
 * 
 * @param input - Raw deal input
 * @param resolvedStartDate - Already resolved start date (for deriving date_phrase)
 * @param resolvedEndDate - Already resolved end date (for deriving date_phrase)
 */
function resolveDatesTypeInfo(
  input: RawDealInput,
  resolvedStartDate?: string | null,
  resolvedEndDate?: string | null
): { 
  datesType: DatesType | null; 
  datePhrase: string | null;
  dateMode: "ONGOING" | "FIXED" | null;
} {
  let explicitDatesType: DatesType | null = null;
  let explicitDatePhrase: string | null = null;
  let explicitDateMode: "ONGOING" | "FIXED" | null = null;
  
  // Priority 1: Top-level fields
  if (input.dates_type || input.date_phrase || (input as Record<string, unknown>).date_mode) {
    explicitDatesType = normalizeDatesType(input.dates_type as string);
    explicitDatePhrase = getSafeString(input.date_phrase);
    explicitDateMode = normalizeDateMode((input as Record<string, unknown>).date_mode as string);
  }
  
  // Priority 2: canonical_deal_json.dates nested object (if not already found)
  const canonical = input.canonical_deal_json;
  if (canonical && (!explicitDatesType && !explicitDatePhrase && !explicitDateMode)) {
    const dates = canonical.dates as Record<string, unknown> | undefined;
    if (dates) {
      if (dates.dates_type || dates.date_phrase || dates.date_mode) {
        explicitDatesType = explicitDatesType || normalizeDatesType(dates.dates_type as string);
        explicitDatePhrase = explicitDatePhrase || getSafeString(dates.date_phrase);
        explicitDateMode = explicitDateMode || normalizeDateMode(dates.date_mode as string);
      }
    }
    // Also check canonical root level
    if (!explicitDatesType && !explicitDatePhrase && !explicitDateMode) {
      if (canonical.dates_type || canonical.date_phrase || canonical.date_mode) {
        explicitDatesType = normalizeDatesType(canonical.dates_type as string);
        explicitDatePhrase = getSafeString(canonical.date_phrase);
        explicitDateMode = normalizeDateMode(canonical.date_mode as string);
      }
    }
  }
  
  // Priority 3: evidence_json dates (if not already found)
  const evidence = input.evidence_json;
  if (evidence && (!explicitDatesType && !explicitDatePhrase && !explicitDateMode)) {
    const evidenceDates = evidence.dates as Record<string, unknown> | undefined;
    if (evidenceDates && (evidenceDates.dates_type || evidenceDates.date_phrase || evidenceDates.date_mode)) {
      explicitDatesType = normalizeDatesType(evidenceDates.dates_type as string);
      explicitDatePhrase = getSafeString(evidenceDates.date_phrase);
      explicitDateMode = normalizeDateMode(evidenceDates.date_mode as string);
    }
  }
  
  // CR313: DERIVATION - Compute dates_type from date_mode if not explicitly set
  let derivedDatesType = explicitDatesType;
  if (!derivedDatesType && explicitDateMode) {
    derivedDatesType = explicitDateMode === "ONGOING" ? "EVERGREEN" : "FIXED_WINDOW";
  }
  // If no mode but we have dates, infer FIXED_WINDOW
  if (!derivedDatesType && resolvedStartDate && resolvedEndDate) {
    derivedDatesType = "FIXED_WINDOW";
  }
  
  // CR313: DERIVATION - Compute date_phrase from dates if not explicitly set
  let derivedDatePhrase = explicitDatePhrase;
  if (!derivedDatePhrase) {
    if (explicitDateMode === "ONGOING" || derivedDatesType === "EVERGREEN") {
      derivedDatePhrase = "Ongoing";
    } else if (resolvedStartDate || resolvedEndDate) {
      // Use formatEligibilityRange for FIXED dates
      derivedDatePhrase = formatEligibilityRange(resolvedStartDate, resolvedEndDate);
    }
  }
  
  return { 
    datesType: derivedDatesType, 
    datePhrase: derivedDatePhrase, 
    dateMode: explicitDateMode 
  };
}

/**
 * Resolve merchant name with fallback chain
 */
function resolveMerchant(input: RawDealInput): string {
  return getSafeString(input.promo_merchant_name) 
    || getSafeString(input.merchant_name)
    || getSafeString(input.merchant)
    || getSafeString(input.merchant_key)
    || getSafeString(input.canonical_deal_json?.merchant_name)
    || getSafeString(input.canonical_deal_json?.merchant_key)
    || "Unknown";
}

/**
 * Resolve reward/gift card brand
 */
function resolveRewardBrand(input: RawDealInput): string | null {
  return getSafeString(input.reward_brand_name)
    || getSafeString(input.purchase_gift_card_brand)
    || getSafeString(input.gift_card_brand)
    || getSafeString(input.bonus_merchant)
    || getSafeString(input.canonical_deal_json?.reward_brand_name)
    || getSafeString(input.canonical_deal_json?.purchase_gift_card_brand)
    || null;
}

/**
 * Resolve headline mechanic text
 */
function resolveHeadline(input: RawDealInput): string {
  const raw = getSafeString(input.headline_mechanic)
    || getSafeString(input.canonical_deal_json?.headline_mechanic);
  
  // Use sanitizeDisplayText to clean up any "(undefined)" patterns
  return sanitizeDisplayText(raw) || "";
}

/**
 * Resolve source type
 */
function resolveSourceType(input: RawDealInput): SourceType {
  const raw = getSafeString(input.source_type)
    || getSafeString(input.canonical_deal_json?.source_type);
  
  const validSources: SourceType[] = ["flyer", "html", "api", "pdf", "email", "other", "website", "terms", "redemption_portal/terms"];
  if (raw && validSources.includes(raw as SourceType)) {
    return raw as SourceType;
  }
  return "website";
}

/**
 * Resolve numeric value with fallback
 */
function resolveNumber(
  primary: number | null | undefined,
  ...fallbacks: (number | null | undefined)[]
): number | null {
  if (typeof primary === "number" && !isNaN(primary)) return primary;
  for (const fb of fallbacks) {
    if (typeof fb === "number" && !isNaN(fb)) return fb;
  }
  return null;
}

/**
 * CR332: Resolve artifacts from deal data.
 * Checks canonical_deal_json.artifacts and top-level artifacts field.
 */
function resolveArtifacts(input: RawDealInput): DealArtifacts | undefined {
  const canonical = input.canonical_deal_json || {};
  
  // Priority: top-level artifacts > canonical_deal_json.artifacts
  const raw = (input as any).artifacts || (canonical as any).artifacts;
  if (!raw || typeof raw !== "object") return undefined;
  
  const result: DealArtifacts = {};
  
  if (raw.flyer_snippet && typeof raw.flyer_snippet === "object") {
    result.flyer_snippet = {
      asset_path: raw.flyer_snippet.asset_path || null,
      page_number: typeof raw.flyer_snippet.page_number === "number" ? raw.flyer_snippet.page_number : null,
    };
  }
  
  // Only return if we have actual data
  return result.flyer_snippet ? result : undefined;
}

// ================================================================
// MAIN MAPPER
// ================================================================

/**
 * Convert any raw deal input into the canonical UIDeal shape.
 * 
 * @param input - Raw deal from API, database, or mock
 * @param sourceMode - Source mode for debugging (optional)
 * @returns Normalized UIDeal ready for rendering
 */
export function toUiDeal(input: RawDealInput, sourceMode?: UIDeal["_sourceMode"]): UIDeal {
  // CR317: canonical_deal_json is guaranteed by RPC (CR316A). No deal_json fallback.
  const canonical = input.canonical_deal_json ?? {};
  
  // Resolve dates from multiple sources (with tier tracking for debugging)
  // CR292: Also extracts dates_type, date_phrase, and date_mode
  const { startDate, endDate, tier: dateSourceTier, datesType, datePhrase, dateMode } = resolveDates(input);
  
  // Resolve category
  const rawCategory = input.deal_category || input.deal_type || canonical.deal_type;
  const dealCategory = normalizeDealCategory(rawCategory as string);
  const dealTypeLower = dealCategory.toLowerCase() as Deal["deal_type"];
  
  // Resolve merchant/brand
  const merchant = resolveMerchant(input);
  const rewardBrand = resolveRewardBrand(input);
  
  // Resolve values
  const bonusValueCad = resolveNumber(
    input.bonus_value_cad,
    input.bonus_amount,
    canonical.bonus_value_cad as number,
    (canonical.value as Record<string, number>)?.bonus_value_cad
  );
  const qualifyingSpendCad = resolveNumber(
    input.qualifying_spend_cad,
    input.spend_amount,
    canonical.qualifying_spend_cad as number,
    (canonical.value as Record<string, number>)?.qualifying_spend_cad
  );
  const computedValueCad = resolveNumber(
    input.computed_value_cad,
    canonical.computed_value_cad as number,
    (canonical.value as Record<string, number>)?.computed_value_cad
  );
  const effectivePercentBack = resolveNumber(
    input.effective_percent_back,
    canonical.effective_percent_back as number,
    (canonical.value as Record<string, number>)?.effective_percent_back
  );
  const discountPercent = resolveNumber(
    input.discount_percent,
    canonical.discount_percent as number,
    (canonical.value as Record<string, number>)?.discount_percent
  );
  const pointsAmount = resolveNumber(
    input.points_amount,
    canonical.points_amount as number
  );
  
  // CR277: Resolve points program name and look up conversion rate
  const pointsProgramName = getSafeString(input.points_program_name) 
    || getSafeString(canonical.points_program_name)
    || getSafeString((canonical.value as Record<string, unknown>)?.points_program);
  
  // CR277: Look up loyalty program info by name to get program ID and conversion rate
  const loyaltyProgram = lookupByProgramName(pointsProgramName);
  const pointsProgramId = loyaltyProgram?.programId || null;
  const pointsConversionRate = loyaltyProgram?.conversionRate || null;
  
  // CR277: Calculate points cash value if we have conversion rate
  const pointsCashValue = pointsAmount && pointsConversionRate 
    ? calculatePointsCashValue(pointsAmount, pointsProgramName)
    : null;
  
  // Resolve action URLs
  const claimUrl = getSafeString(input.claim_url) || getSafeString(canonical.claim_url);
  const dealUrl = getSafeString(input.deal_url) || getSafeString(input.deal_link) || getSafeString(canonical.deal_link);
  const flyerDeeplinkUrl = getSafeString(input.flyer_deeplink_url) || getSafeString(canonical.flyer_deeplink_url);
  
  // Resolve fine print
  const finePrint = sanitizeDisplayText(input.fine_print) || sanitizeDisplayText(canonical.fine_print);
  const exclusions = sanitizeDisplayText(input.exclusions) || sanitizeDisplayText(canonical.exclusions);
  
  // Build the UIDeal
  const deal: UIDeal = {
    // Internal tracking
    _previewId: input.id || input.dedupe_key || `deal-${Math.random().toString(36).slice(2)}`,
    _reviewStatus: input.review_status,
    _sourceMode: sourceMode,
    _dateSourceTier: dateSourceTier,
    
    // Core identification
    merchant,
    
    // CR099: Semantic merchant/brand
    promo_merchant_name: merchant,
    reward_brand_name: rewardBrand,
    // CR417: Preserve server-computed heading brand for DealGiftCard title resolution
    header_brand_name: getSafeString(input.header_brand_name) || null,
    nested_purchase_brand: getSafeString(input.nested_purchase_brand)
      || getSafeString((canonical.brands as Record<string, unknown>)?.purchase_brand)
      || null,
    
    // CR278/CR279: Qualifying product name (the actual gift card brand for BONUS deals)
    // Priority: qualifying_product_name → nested_purchase_brand → canonical fields → purchase_gift_card_brand
    qualifying_product_name: getSafeString(input.qualifying_product_name) 
      || getSafeString(input.nested_purchase_brand)
      || getSafeString(canonical.qualifying_product_name)
      || getSafeString((canonical.brands as Record<string, unknown>)?.purchase_brand)
      || getSafeString(input.purchase_gift_card_brand)
      || null,
    
    // Deal type
    deal_category: dealCategory,
    deal_type: dealTypeLower,
    headline_mechanic: resolveHeadline(input),
    
    // Purchase gift card details
    purchase_gift_card_brand: getSafeString(input.purchase_gift_card_brand) || getSafeString(canonical.purchase_gift_card_brand) || rewardBrand,
    purchase_brand_logo_url: null,
    
    // Bonus fields
    bonus_merchant: getSafeString(input.bonus_merchant) || getSafeString(canonical.bonus_merchant),
    bonus_merchant_logo_url: null,
    bonus_amount: bonusValueCad,
    spend_amount: qualifyingSpendCad,
    bonus_type: undefined,
    qualifying_spend_cad: qualifyingSpendCad,
    bonus_value_cad: bonusValueCad,
    
    // Legacy fields
    gift_card_brand: getSafeString(input.gift_card_brand) || rewardBrand,
    bonus_or_discount_amount: bonusValueCad,
    
    // Discount fields — CR313: resolve discount_amount_cad from canonical value
    discount_subtype: undefined,
    pay_amount_cad: resolveNumber(
      input.pay_amount_cad,
      (canonical.value as Record<string, number>)?.pay_amount_cad
    ),
    receive_value_cad: resolveNumber(
      input.receive_value_cad,
      (canonical.value as Record<string, number>)?.receive_value_cad
    ),
    discount_amount_cad: resolveNumber(
      input.discount_amount_cad,
      (canonical.value as Record<string, number>)?.discount_amount_cad,
      (canonical.value as Record<string, number>)?.discount_value_cad
    ),
    min_purchase_cad: null,
    spend_range_min: null,
    spend_range_max: null,
    discount_percent: discountPercent,
    discount_tiers: null,
    deal_pricing_type: null,
    
    // Points fields - CR277: Use loyalty program lookup for ID and conversion rate
    points_amount: pointsAmount,
    points_program_id: pointsProgramId,
    points_program_name: pointsProgramName,
    points_to_currency_rate: pointsConversionRate,
    
    // In-Kind fields
    in_kind_tiers: null,
    
    // Valuation - CR277: Use points cash value for POINTS deals
    valuation_source: undefined,
    unit_value_cad: null,
    valuation_reference_url: null,
    valuation_notes: null,
    computed_value_cad: dealCategory === "POINTS" && pointsCashValue 
      ? pointsCashValue.cashValue 
      : computedValueCad,
    effective_percent_back: dealCategory === "POINTS" && pointsCashValue && qualifyingSpendCad
      ? (pointsCashValue.cashValue / qualifyingSpendCad) * 100
      : effectivePercentBack,
    normalized_value_score: dealCategory === "POINTS" && pointsCashValue 
      ? pointsCashValue.cashValue 
      : computedValueCad,
    
    // Dates
    start_date: startDate,
    end_date: endDate,
    bonus_valid_from: null,
    bonus_valid_until: null,
    purchase_window_start: null,
    purchase_window_end: null,
    redeem_window_start: null,
    redeem_window_end: null,
    
    // CR292: Date type classification for evergreen/unknown deals
    dates_type: datesType || undefined,
    date_phrase: datePhrase,
    // CR292: Date mode (ONGOING = evergreen, FIXED = has dates)
    date_mode: dateMode || undefined,
    claim_deadline: null,
    
    // CR415: Issuer fields (from enriched view or admin preview)
    issuer_display_name: getSafeString(input.issuer_display_name) || getSafeString(canonical.issuer_display_name) || null,
    issuer_logo_url: getSafeString(input.issuer_logo_url) || getSafeString(canonical.issuer_logo_url) || null,
    issuer_merchant_key: getSafeString(input.issuer_merchant_key) || getSafeString(canonical.issuer_merchant_key) || null,
    
    // Source info
    source_url: getSafeString(input.source_url) || getSafeString(canonical.source_url),
    source_type: resolveSourceType(input),
    flyer_deeplink_url: flyerDeeplinkUrl,
    flyer_page_number: resolveNumber(input.flyer_page_number, canonical.flyer_page_number as number),
    flyer_page_position: (getSafeString(input.flyer_page_position) || getSafeString(canonical.flyer_page_position) || null) as Deal["flyer_page_position"],
    raw_source_notes: null,
    
    // Actions
    claim_url: claimUrl,
    deal_url: dealUrl,
    
    // Meta
    fine_print: finePrint,
    exclusions: exclusions,
    disclaimer_required: false,
    disclaimer_text: null,
    requires_physical_card: false,
    proof_urls: input.source_url ? [input.source_url] : [],
    confidence: normalizeConfidence(input.confidence),
    confidence_reason: null,
    region: getSafeString(input.region) || "ON",
    channel: "unknown",
    redeem_url: null,
    campaign_name: null,
    
    // Location/Eligibility
    availability_scope: (getSafeString(input.availability_scope) || getSafeString(canonical.availability_scope) || "unknown") as AvailabilityScope,
    availability_notes: getSafeString(input.availability_notes) || getSafeString(canonical.availability_notes),
    eligibility_scope: (getSafeString(input.eligibility_scope) || getSafeString(canonical.eligibility_scope)) as EligibilityScope | undefined,
    audience_hint: (getSafeString(input.audience_hint) || getSafeString(canonical.audience_hint)) as AudienceHint | undefined,
    
    // CR332: Artifacts (flyer snippets, etc.)
    artifacts: resolveArtifacts(input),
  };
  
  // CR313: Hydrate canonical_deal_json for V2 block resolvers.
  // Ensure nested value + dates objects exist so blocks like ValueBoxBlock
  // and DatesBlock can read deal.canonical_deal_json.value.* / .dates.*
  if (input.canonical_deal_json) {
    const cdj = { ...input.canonical_deal_json };
    
    // Ensure value object is hydrated with resolved top-level fields
    const existingValue = (cdj.value as Record<string, unknown>) ?? {};
    cdj.value = {
      ...existingValue,
      qualifying_spend_cad: existingValue.qualifying_spend_cad ?? deal.qualifying_spend_cad ?? null,
      computed_value_cad: existingValue.computed_value_cad ?? deal.computed_value_cad ?? null,
      effective_percent_back: existingValue.effective_percent_back ?? deal.effective_percent_back ?? null,
      discount_amount_cad: existingValue.discount_amount_cad ?? existingValue.discount_value_cad ?? deal.discount_amount_cad ?? null,
      bonus_value_cad: existingValue.bonus_value_cad ?? deal.bonus_value_cad ?? null,
    };
    
    // Ensure dates object is hydrated with YYYY-MM-DD strings
    const existingDates = (cdj.dates as Record<string, unknown>) ?? {};
    const dateFrom = toDateOnlyString(existingDates.valid_from as string) 
      ?? toDateOnlyString(deal.start_date);
    const dateUntil = toDateOnlyString(existingDates.valid_until as string) 
      ?? toDateOnlyString(deal.end_date);
    cdj.dates = {
      ...existingDates,
      valid_from: dateFrom ?? existingDates.valid_from ?? null,
      valid_until: dateUntil ?? existingDates.valid_until ?? null,
    };
    
    deal.canonical_deal_json = cdj;
  }
  
  // CR434: Force id last — preserve real UUID when present.
  // Never use dedupe_key as id (not a UUID).
  const resolvedId =
    input.id ??
    (input as any).deal_id ??
    (input as any).canonical_deal_json?.id ??
    deal._previewId ??
    null;

  if (import.meta.env.DEV) {
    const v = resolvedId;
    const looksUuid = typeof v === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
    if (v && !looksUuid && !String(v).startsWith("sample:")) {
      console.warn("[CR434] Non-UUID id produced by toUiDeal", { resolvedId: v, input });
    }
  }

  return { ...deal, id: resolvedId ?? "missing-id" } as UIDeal;
}

/**
 * Batch convert multiple raw deals
 */
export function toUiDeals(inputs: RawDealInput[], sourceMode?: UIDeal["_sourceMode"]): UIDeal[] {
  return inputs.map(input => toUiDeal(input, sourceMode));
}

// ================================================================
// VALIDATION HELPERS
// ================================================================

/**
 * Check if a UIDeal has the minimum required fields for rendering
 */
export function isRenderReady(deal: UIDeal): boolean {
  return isSafeValue(deal.merchant) && deal.merchant !== "Unknown";
}

/**
 * Get reasons why a deal might not render properly
 */
export function getDealRenderIssues(deal: UIDeal): string[] {
  const issues: string[] = [];
  
  if (!isSafeValue(deal.merchant) || deal.merchant === "Unknown") {
    issues.push("missing_merchant");
  }
  if (!isSafeValue(deal.headline_mechanic)) {
    issues.push("missing_headline");
  }
  // CR292: Suppress missing_dates issue for EVERGREEN/UNKNOWN with date_phrase
  // Also suppress for date_mode="ONGOING" which indicates evergreen deal
  const isOngoing = deal.date_mode === "ONGOING";
  const isEvergreen = deal.dates_type === "EVERGREEN";
  const hasDatePhrase = deal.dates_type === "UNKNOWN" && deal.date_phrase;
  const shouldWarnMissingDates = !deal.start_date && !deal.end_date 
    && !isOngoing 
    && !isEvergreen 
    && !hasDatePhrase;
  if (shouldWarnMissingDates) {
    issues.push("missing_dates");
  }
  if (deal.deal_category === "UNCLASSIFIED") {
    issues.push("unclassified_type");
  }
  
  return issues;
}
